import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { SUPPORTED_LOCALES, useTranslation } from "@/i18n";

/** Compact globe-style locale picker for the public header.
 *  Stores the choice in localStorage via the i18n module. */
export default function LanguagePicker() {
  const [, locale, setLocale] = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const current = SUPPORTED_LOCALES.find((l) => l.code === locale) || SUPPORTED_LOCALES[0];
  return (
    <div className="relative" ref={ref} data-testid="lang-picker">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 hover:border-white/30 transition-colors text-sm"
        data-testid="lang-picker-toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span aria-hidden="true">{current.flag}</span>
        <span className="hidden sm:inline">{current.name}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul
          className="absolute right-0 mt-2 w-44 rounded-xl border border-white/10 bg-[var(--surface-2)] backdrop-blur-md py-1 shadow-2xl z-50"
          role="listbox"
          data-testid="lang-picker-menu"
        >
          {SUPPORTED_LOCALES.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                onClick={() => { setLocale(l.code); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-white/5 ${
                  l.code === locale ? "text-[color:var(--accent)]" : "text-white"
                }`}
                data-testid={`lang-option-${l.code}`}
              >
                <span aria-hidden="true">{l.flag}</span>
                <span>{l.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
